import update from 'react-addons-update';
import {
  ISSUES_COLLECTION_RESET,
  ISSUES_FETCH_END,
  ISSUES_FETCH_API_PENDING,
  ISSUES_FETCH_PAYLOAD_INJECT,
  ISSUES_FETCH_LOADING_START,
  ISSUES_FETCH_API_REJECTED,
  ISSUE_INSTANCE_FETCH_API_PENDING,
  ISSUE_INSTANCE_FETCH_API_REJECTED,
  ISSUE_INSTANCE_FETCH_PAYLOAD_INJECT,
  RFI_CREATE_API_PENDING,
  RFI_CREATE_API_FULFILLED,
  RFI_CREATE_API_REJECTED,
  RFI_UPDATE_API_PENDING,
  RFI_UPDATE_API_FULFILLED,
  RFI_UPDATE_API_REJECTED,
  REMOVE_ISSUE_FROM_COLLECTION,
  CHANNEL_ITEMS_FETCH_API_PENDING,
  CHANNEL_ITEMS_FETCH_API_FULFILLED,
  CHANNEL_ITEMS_FETCH_API_REJECTED,
  COMMENT_CREATE_API_PENDING,
  COMMENT_CREATE_API_REJECTED,
  ISSUES_FETCH_SEARCH_API_FULFILLED,
  ISSUES_CLEAR_SEARCH,
  CREATE_RFI_PROCESS_START,
  CREATE_RFI_PROCESS_END,
  REMOVE_RFI_FROM_LIST,
  ADD_LINKED_ENTITIES_API_FULFILLED,
  REMOVE_LINKED_ENTITIES_API_FULFILLED,
} from '../actions';
import { isBadPermissionStatus } from '../../utils/jsonapi-helpers';
import lodashGet from 'lodash/get';
import union from 'lodash/union';

const initialChannelItems = {
  loading: false,
  error: null,
  sortOrder: [],
  collection: {},
};

const addDeletedAttachmentsChanges = (itemProps) => {
  if (itemProps.collection) {
    const collection = {};
    Object.keys(itemProps.collection).map(k => {
      const item = itemProps.collection[k];
      collection[k] = item;
      // When getting an attachment with `deletedAt` - we need to create additional event indicating attachment deletion.
      if (item.type === 'attachments' && item.deletedAt) {
        const deleteId = `${k}-delete`;
        collection[deleteId] = { ...item, id: deleteId, isDeleteAttachmentChange: true };
      }
    });
    return { ...itemProps, collection };
  }
  return itemProps;
};

const sortByCreatedAt = function(a, b) {
  const aValue = a.isDeleteAttachmentChange ? a.deletedAt : a.createdAt;
  const bValue = b.isDeleteAttachmentChange ? b.deletedAt : b.createdAt;
  return aValue === bValue ? 0 : aValue > bValue ? 1 : -1;
};

const injectItems = function(collectionName, issuesState, issueId, itemProps) {
  const targetIssueWrapper = issuesState[issueId];
  const transformedItemProps = addDeletedAttachmentsChanges(itemProps);
  if (targetIssueWrapper) {
    const updatedItems = update(targetIssueWrapper[collectionName], { $merge: transformedItemProps });
    const itemsArray = Object.keys(updatedItems.collection).map(key => updatedItems.collection[key]);
    updatedItems.sortOrder = itemsArray.sort(sortByCreatedAt).map(channelItem => channelItem.id);
    return update(issuesState, {
      [issueId]: { $merge: update(targetIssueWrapper, { $merge: { [collectionName]: updatedItems }}) },
    });
  } else {
    // items are not injected, if current issue id is not in issuesState:
    return issuesState;
  }
};

// Shared by Issues Collection and Issue instances:
const injectChannelItems = function(issuesState, issueId, commentProps) {
  // channel-items were, at one point, separated by type.  They are now consolidated under a single 'channelItems'
  // property, in each issue-wrapper -- this could be reverted to multiple collections, via the 1st argument:
  if (lodashGet(issuesState[issueId], 'channelItems')) {
    return injectItems('channelItems', issuesState, issueId, commentProps);
  }

  return injectItems('channelItems', update(issuesState, { [issueId]: { $merge: { channelItems: initialChannelItems }}}), issueId, commentProps);
};


const injectIssue = function(issuesState, issueWrapper) {
  const id = issueWrapper.id;
  delete issueWrapper.id;
  const targetIssueWrapper = issuesState[id];
  const changeToStatus = issueWrapper.changeToStatus;
  const command = targetIssueWrapper ? '$merge' : '$set';

  // We don't want to "loose" any attributes that already exist (in case the issue was fetched as an instance before
  // it was fetched as part of a collection) so we need to merge attributes first and return the updated state after
  if (targetIssueWrapper && targetIssueWrapper.attributes && issueWrapper.attributes) {
    const completeAttributes = update(targetIssueWrapper.attributes, { $merge: issueWrapper.attributes });
    issueWrapper.attributes = completeAttributes;
  }

  //changeToStatus is bean send by transition to the status
  if (changeToStatus) {
    issueWrapper.attributes.changeToStatus = changeToStatus;
  }

  return update(issuesState, { [id]: { [command]: issueWrapper }});
};


/* eslint-disable default-case */

// Issue Collection:
const issuesLoadingReducer = (state = false, action) => {
  switch (action.type) {
    case ISSUES_FETCH_API_PENDING:
      return true;
    case ISSUES_FETCH_PAYLOAD_INJECT:
    case ISSUES_FETCH_API_REJECTED:
      return false;
  }
  return state;
};

const issuesErrorReducer = (state = null, action) => {
  switch (action.type) {
    case ISSUES_FETCH_API_PENDING:
    case ISSUES_FETCH_PAYLOAD_INJECT:
      return null;
    case ISSUES_FETCH_API_REJECTED:
      return action.payload;
  }
  return state;
};

const lastUpdatedKeyReducer = (state = '', action) => {
  switch (action.type) {
    case RFI_UPDATE_API_FULFILLED:
    case RFI_CREATE_API_FULFILLED:
      return ['id', 'updatedAt'].map(attributeName => action.payload[attributeName]).join('-');
  }
  return state;
};

const issuesLastQueryKeyReducer = (state = {}, action) => {
  switch (action.type) {
    case ISSUES_FETCH_API_PENDING: {
      const { meta: { query: { offset }, queryKey }} = action;
      if (offset === 0) {
        return update(state, { $set: queryKey });
      }
    }
  }
  return state;
};

const issuesSortOrderReducer = (state = [], action) => {
  switch (action.type) {
    case ISSUES_COLLECTION_RESET: {
      // TODO: Only reset when jsonapi query has changed (ignoring offset)
      // naive implementation -- always reset:

      const { currentlyViewedId } = action.payload;

      return state.filter((issueId) => currentlyViewedId === issueId);
    }
    case ISSUES_FETCH_PAYLOAD_INJECT:
      // For internal views, use the default order from the jsonapi responses:
      return union(
        state.slice(0),
        action.payload.map(({ id }) => id),
      );
    case RFI_CREATE_API_FULFILLED:
      return update(state, { $unshift: [action.payload.id]});
    case REMOVE_RFI_FROM_LIST:
    {
      const { rfiId } = action.payload;
      return state.filter(id => id !== rfiId);
    }
  }
  return state;
};

const issuesBatchLoadingReducer = (state = false, action) => {
  switch (action.type) {
    case ISSUES_FETCH_LOADING_START:
      return update(state, { $set: true });
    case ISSUES_FETCH_END:
      return update(state, { $set: false });
  }
  return state;
};

const issuesCollectionReducer = (issuesState = {}, action) => {
  switch (action.type) {
    case ISSUES_COLLECTION_RESET: {
      // Remove issues from state by issue type
      const updatedCollection = {};
      const { currentlyViewedId } = action.payload;

      // eslint-disable-next-line no-restricted-syntax
      for (const issueId in issuesState) {
        if (currentlyViewedId === issueId) {
          updatedCollection[issueId] = issuesState[issueId];
        }
      }

      return updatedCollection;
    }
    case ISSUES_FETCH_API_PENDING:
      return issuesState;
    case ISSUES_FETCH_PAYLOAD_INJECT:
      return action.payload.reduce((map, attributes) => {
        // inject one issue at a time:
        return injectIssue(map, {
          id: attributes.id,
          loading: false,
          updating: false,
          error: null,
          attributes,
        });
      }, issuesState);
    case ISSUE_INSTANCE_FETCH_PAYLOAD_INJECT:
      return injectIssue(issuesState, {
        id: action.meta.id,
        loading: false,
        updating: false,
        error: null,
        attributes: action.payload,
      });
    case ISSUES_FETCH_API_REJECTED:
      return issuesState;
    case ISSUE_INSTANCE_FETCH_API_PENDING:
      return injectIssue(issuesState, {
        id: action.meta.id,
        loading: true,
        updating: false,
        error: null,
      });
    case ISSUE_INSTANCE_FETCH_API_REJECTED: {
      if (isBadPermissionStatus(action.payload.response.status)) {
        const newIssuesState = Object.assign({}, issuesState);
        delete newIssuesState[action.meta.id];
        return newIssuesState;
      }
      return injectIssue(issuesState, {
        id: action.meta.id,
        loading: false,
        updating: false,
        error: action.payload,
      });
    }
    case RFI_UPDATE_API_PENDING:
      return injectIssue(issuesState, {
        id: action.meta.id,
        updating: true,
        error: null,
      });
    case RFI_UPDATE_API_REJECTED:
      return injectIssue(issuesState, {
        id: action.meta.id,
        updating: false,
        error: action.payload,
      });
    case RFI_UPDATE_API_FULFILLED:
      return injectIssue(issuesState, {
        id: action.meta.id,
        changeToStatus: lodashGet(action.meta, 'changeToStatus'),
        updating: false,
        error: null,
        attributes: Object.assign({}, action.meta.attributes, action.payload),
      });
    case REMOVE_ISSUE_FROM_COLLECTION: {
      const newIssuesState = Object.assign({}, issuesState);
      delete newIssuesState[action.payload];

      return newIssuesState;
    }
    case RFI_CREATE_API_FULFILLED:
      return injectIssue(issuesState, {
        id: action.payload.id,
        updating: false,
        loading: false,
        error: null,
        attributes: action.payload,
        // INITIAL CHANNEL STATE:
        channelItems: initialChannelItems,
      });
    case RFI_CREATE_API_PENDING:
    case RFI_CREATE_API_REJECTED:
      break;
    case CHANNEL_ITEMS_FETCH_API_PENDING:
    case COMMENT_CREATE_API_PENDING:
      return injectChannelItems(issuesState, action.meta.id, {
        loading: true,
        error: null,
      });
    case CHANNEL_ITEMS_FETCH_API_REJECTED:
    case COMMENT_CREATE_API_REJECTED:
      return injectChannelItems(issuesState, action.meta.id, {
        loading: false,
        error: action.payload,
      });
    case CHANNEL_ITEMS_FETCH_API_FULFILLED: {
      return injectChannelItems(issuesState, action.meta.id, {
        loading: false,
        error: null,
        sortOrder: [],
        collection: action.payload.reduce((map, channelItem) => { map[channelItem.id] = channelItem; return map; }, {}),
      });
    }

    case ADD_LINKED_ENTITIES_API_FULFILLED:
    case REMOVE_LINKED_ENTITIES_API_FULFILLED: {
      return injectIssue(issuesState, {
        id: action.meta.id,
        attributes: action.meta.attributes,
      });
    }
  }
  return issuesState;
};

const issuesCollectionPaginationReducer = (state = null, action) => {
  switch (action.type) {
    case ISSUES_FETCH_PAYLOAD_INJECT:
      if (!action.payload.length)
        break;
      const { queryParams } = action.meta.query;
      const { pathname, queryParamsString } = queryParams.currentLocation;
      if (!action.payload.length) {
        return state;
      }
      const { page: { offset, limit }, recordCount } = action.payload[0].meta;
      const lastOffset = recordCount ? Math.floor((recordCount - 1) / limit) * limit : 0;
      const links = {
        first: `${pathname}?${queryParamsString}`,
        last: `${pathname}?${queryParamsString}&page[offset]=${lastOffset}`,
      };
      if (offset !== 0) {
        links.prev = `${pathname}?${queryParamsString}&page[offset]=${offset - limit}`;
      }
      if (offset !== lastOffset) {
        links.next = `${pathname}?${queryParamsString}&page[offset]=${offset + limit}`;
      }
      return {
        meta: action.payload[0].meta,
        links,
      };
  }
  return state;
};

const issuesCollectionSearchResultsReducer = (state = null, action) => {
  switch (action.type) {
    case ISSUES_FETCH_SEARCH_API_FULFILLED:
      return action.payload;
    case ISSUES_CLEAR_SEARCH:
      return [];
  }
  return state;
};

const isInCreateRfiProcessReducer = (state = false, action) => {
  switch (action.type) {
    case CREATE_RFI_PROCESS_START:
      return true;
    case CREATE_RFI_PROCESS_END:
      return false;
  }
  return state;
};

export default (state = {}, action) => {
  return {
    // Is the batch-fetch for entire collection still loading?
    batchLoading: issuesBatchLoadingReducer(state.batchLoading, action),
    // Is the the single, one-at-a-time, sub-batch still loading?
    loading: issuesLoadingReducer(state.loading, action),
    error: issuesErrorReducer(state.error, action),
    lastUpdatedKey: lastUpdatedKeyReducer(state.lastUpdatedKey, action),
    lastQueryKey: issuesLastQueryKeyReducer(state.lastQueryKey, action),
    sortOrder: issuesSortOrderReducer(state.sortOrder, action),
    collection: issuesCollectionReducer(state.collection, action),
    pagination: issuesCollectionPaginationReducer(state.pagination, action),
    searchResults: issuesCollectionSearchResultsReducer(state.searchResults, action),
    isInCreateRfiProcess: isInCreateRfiProcessReducer(state.isInCreateRfiProcess, action),
  };
};
